import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { CampaignProvider } from "@/contexts/CampaignContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { initAnalytics, trackPageView } from "@/lib/analytics";

import Home from "@/pages/public/Home";
import Catalog from "@/pages/public/Catalog";
import ProductDetail from "@/pages/public/ProductDetail";
import CategoryPage from "@/pages/public/CategoryPage";
import BundleDetail from "@/pages/public/BundleDetail";
import CheckoutReturn from "@/pages/public/CheckoutReturn";
import DesktopDownload from "@/pages/public/DesktopDownload";
import Benefits from "@/pages/public/Benefits";
import Artists from "@/pages/public/Artists";
import Cart from "@/pages/public/Cart";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import ClientDashboard from "@/pages/client/ClientDashboard";
import MyPurchases from "@/pages/client/MyPurchases";
import MyPlugins from "@/pages/client/MyPlugins";
import RedeemSerial from "@/pages/client/RedeemSerial";
import MyDownloads from "@/pages/client/MyDownloads";
import MySupport from "@/pages/client/MySupport";
import MyLicenses from "@/pages/client/MyLicenses";
import MySubscriptions from "@/pages/client/MySubscriptions";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminInstallers from "@/pages/admin/AdminInstallers";
import AdminPurchases from "@/pages/admin/AdminPurchases";
import AssignPurchase from "@/pages/admin/AssignPurchase";
import AssignToArtist from "@/pages/admin/AssignToArtist";
import AdminSerials from "@/pages/admin/AdminSerials";
import AdminDownloads from "@/pages/admin/AdminDownloads";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminLicenses from "@/pages/admin/AdminLicenses";
import AdminUsbKeys from "@/pages/admin/AdminUsbKeys";
import AdminSpecialLicenses from "@/pages/admin/AdminSpecialLicenses";
import AdminBundles from "@/pages/admin/AdminBundles";
import AdminArtists from "@/pages/admin/AdminArtists";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminCampaign from "@/pages/admin/AdminCampaign";
import AdminDesktop from "@/pages/admin/AdminDesktop";

function AnalyticsListener() {
    const location = useLocation();
    useEffect(() => { trackPageView(location.pathname + location.search); }, [location]);
    return null;
}

function App() {
    useEffect(() => { initAnalytics(); }, []);
    return (
        <div className="App min-h-screen bg-avx-bg text-avx-text">
            <AuthProvider>
                <CartProvider>
                <CampaignProvider>
                <BrowserRouter>
                    <AnalyticsListener />
                    <Routes>
                        {/* Public */}
                        <Route path="/" element={<Home />} />
                        <Route path="/plugins" element={<Catalog />} />
                        <Route path="/plugins/categoria/:slug" element={<CategoryPage />} />
                        <Route path="/plugins/:slug" element={<ProductDetail />} />
                        <Route path="/bundles/:slug" element={<BundleDetail />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout/return" element={<CheckoutReturn />} />
                        <Route path="/desktop" element={<DesktopDownload />} />
                        <Route path="/benefits" element={<Benefits />} />
                        <Route path="/artistas" element={<Artists />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        {/* Client */}
                        <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
                        <Route path="/dashboard/purchases" element={<ProtectedRoute><MyPurchases /></ProtectedRoute>} />
                        <Route path="/dashboard/plugins" element={<ProtectedRoute><MyPlugins /></ProtectedRoute>} />
                        <Route path="/dashboard/redeem" element={<ProtectedRoute><RedeemSerial /></ProtectedRoute>} />
                        <Route path="/dashboard/downloads" element={<ProtectedRoute><MyDownloads /></ProtectedRoute>} />
                        <Route path="/dashboard/licenses" element={<ProtectedRoute><MyLicenses /></ProtectedRoute>} />
                        <Route path="/dashboard/subscriptions" element={<ProtectedRoute><MySubscriptions /></ProtectedRoute>} />
                        <Route path="/dashboard/support" element={<ProtectedRoute><MySupport /></ProtectedRoute>} />

                        {/* Admin */}
                        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
                        <Route path="/admin/products" element={<ProtectedRoute role="admin"><AdminProducts /></ProtectedRoute>} />
                        <Route path="/admin/categories" element={<ProtectedRoute role="admin"><AdminCategories /></ProtectedRoute>} />
                        <Route path="/admin/reviews" element={<ProtectedRoute role="admin"><AdminReviews /></ProtectedRoute>} />
                        <Route path="/admin/installers" element={<ProtectedRoute role="admin"><AdminInstallers /></ProtectedRoute>} />
                        <Route path="/admin/purchases" element={<ProtectedRoute role="admin"><AdminPurchases /></ProtectedRoute>} />
                        <Route path="/admin/assign" element={<ProtectedRoute role="admin"><AssignPurchase /></ProtectedRoute>} />
                        <Route path="/admin/assign-artist" element={<ProtectedRoute role="admin"><AssignToArtist /></ProtectedRoute>} />
                        <Route path="/admin/serials" element={<ProtectedRoute role="admin"><AdminSerials /></ProtectedRoute>} />
                        <Route path="/admin/licenses" element={<ProtectedRoute role="admin"><AdminLicenses /></ProtectedRoute>} />
                        <Route path="/admin/usb-keys" element={<ProtectedRoute role="admin"><AdminUsbKeys /></ProtectedRoute>} />
                        <Route path="/admin/special-licenses" element={<ProtectedRoute role="admin"><AdminSpecialLicenses /></ProtectedRoute>} />
                        <Route path="/admin/bundles" element={<ProtectedRoute role="admin"><AdminBundles /></ProtectedRoute>} />
                        <Route path="/admin/artists" element={<ProtectedRoute role="admin"><AdminArtists /></ProtectedRoute>} />
                        <Route path="/admin/coupons" element={<ProtectedRoute role="admin"><AdminCoupons /></ProtectedRoute>} />
                        <Route path="/admin/campaign" element={<ProtectedRoute role="admin"><AdminCampaign /></ProtectedRoute>} />
                        <Route path="/admin/desktop" element={<ProtectedRoute role="admin"><AdminDesktop /></ProtectedRoute>} />
                        <Route path="/admin/downloads" element={<ProtectedRoute role="admin"><AdminDownloads /></ProtectedRoute>} />
                        <Route path="/admin/support" element={<ProtectedRoute role="admin"><AdminSupport /></ProtectedRoute>} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <FloatingWhatsApp />
                </BrowserRouter>
                <Toaster
                    theme="dark"
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: "#0E0E0E",
                            border: "1px solid #1F1F1F",
                            color: "#F5F5F5",
                            fontFamily: "IBM Plex Sans, sans-serif",
                        },
                    }}
                />
                </CampaignProvider>
                </CartProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
